class SearchForm extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.form = this.input?.form;

    this.onFormReset = this.onFormReset.bind(this);
    this.onInput = debounce(this.onChange.bind(this), 300);

    this.form?.addEventListener("reset", this.onFormReset);
    this.input?.addEventListener("input", this.onInput);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("reset", this.onFormReset);
    this.input?.removeEventListener("input", this.onInput);
  }

  toggleResetButton() {
    if (!this.resetButton || !this.input) return;

    this.resetButton.classList.toggle("hidden", this.input.value.length === 0);
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !this.closest("predictive-search")?.querySelector(
      '[aria-selected="true"] a',
    );
  }

  onFormReset(event) {
    event.preventDefault();

    if (!this.input || !this.shouldResetForm()) return;

    this.input.value = "";
    this.input.focus({ preventScroll: true });
    this.toggleResetButton();
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

class SearchOverlay extends HTMLElement {
  constructor() {
    super();

    this.onToggle = this.onToggle.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onReset = this.onReset.bind(this);
  }

  connectedCallback() {
    this.detailsModal = this.closest("details-modal");
    this.details = this.closest("details");
    this.searchModal = this.closest(".search-modal");
    this.backdrop = this.searchModal?.querySelector(
      "[data-search-overlay-close]",
    );
    this.summary = this.details?.querySelector("summary");
    this.form = this.querySelector("form[role='search']");
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.featuredResults = this.querySelector("[data-search-featured]");
    this.liveResults = this.querySelector("[data-search-live-results]");
    this.resultsTitle = this.querySelector("[data-search-results-title]");
    this.resultsCount = this.querySelector("[data-search-results-count]");
    this.status = this.querySelector("[data-search-status]");
    this.resultsLimit = Math.min(
      Math.max(Number.parseInt(this.dataset.resultsLimit || "5", 10), 1),
      10,
    );
    this.currency =
      this.dataset.currency || window.Shopify?.currency?.active || "EUR";

    this.details?.addEventListener("toggle", this.onToggle);
    this.input?.addEventListener("input", this.onInput);
    this.form?.addEventListener("reset", this.onReset);
    this.backdrop?.addEventListener("click", this.onClick);
    this.addEventListener("click", this.onClick);
    document.addEventListener("keydown", this.onKeydown);

    this.showFeaturedResults();
    this.updateOpenState();
  }

  disconnectedCallback() {
    this.details?.removeEventListener("toggle", this.onToggle);
    this.input?.removeEventListener("input", this.onInput);
    this.form?.removeEventListener("reset", this.onReset);
    this.backdrop?.removeEventListener("click", this.onClick);
    this.removeEventListener("click", this.onClick);
    document.removeEventListener("keydown", this.onKeydown);

    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
  }

  onToggle() {
    this.updateOpenState();

    if (!this.details?.open || !this.input) return;

    requestAnimationFrame(() => {
      this.input.focus({ preventScroll: true });
      const inputLength = this.input.value.length;
      this.input.setSelectionRange(inputLength, inputLength);
    });
  }

  onInput() {
    if (!this.input) return;

    const query = this.input.value.trim();
    this.resetButton?.classList.toggle("hidden", query.length === 0);

    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();

    if (!query) {
      this.showFeaturedResults();
      return;
    }

    this.showLoading(query);
    this.searchTimer = window.setTimeout(() => this.fetchProducts(query), 180);
  }

  onReset(event) {
    event.preventDefault();

    if (!this.input) return;

    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.input.value = "";
    this.input.focus({ preventScroll: true });
    this.resetButton?.classList.add("hidden");
    this.showFeaturedResults();
  }

  onClick(event) {
    const closeButton = event.target.closest(
      "[data-search-overlay-close], .modal__close-button",
    );
    if (closeButton) {
      this.closeOverlay();
      return;
    }

    const popularLink = event.target.closest("[data-search-term]");
    if (!popularLink || !this.contains(popularLink) || !this.input) return;

    const searchTerm = popularLink.dataset.searchTerm?.trim();
    if (!searchTerm) return;

    event.preventDefault();
    this.input.value = searchTerm;
    this.resetButton?.classList.remove("hidden");
    this.input.focus({ preventScroll: true });
    this.showLoading(searchTerm);

    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.fetchProducts(searchTerm);
  }

  onKeydown(event) {
    if (event.key !== "Escape" || !this.details?.open) return;

    event.preventDefault();
    this.closeOverlay(true);
  }

  closeOverlay(restoreFocus = false) {
    if (typeof this.detailsModal?.close === "function") {
      this.detailsModal.close(false);
    } else {
      this.details?.removeAttribute("open");
    }

    this.updateOpenState();

    if (restoreFocus) {
      this.summary?.focus();
    }
  }

  updateOpenState() {
    const isOpen = Boolean(this.details?.open);
    document.documentElement.classList.toggle("search-modal-open", isOpen);
    document.body.classList.toggle("search-modal-open", isOpen);
  }

  showFeaturedResults() {
    this.featuredResults?.removeAttribute("hidden");
    this.liveResults?.setAttribute("hidden", "");
    this.liveResults?.replaceChildren();

    if (this.resultsTitle) {
      this.resultsTitle.textContent =
        this.resultsTitle.dataset.defaultTitle || "Bestseller";
    }

    if (this.resultsCount) {
      this.resultsCount.hidden = true;
      this.resultsCount.textContent = "";
    }

    if (this.status) {
      this.status.textContent = "";
    }
  }

  showLoading(query) {
    this.featuredResults?.setAttribute("hidden", "");
    this.liveResults?.removeAttribute("hidden");

    if (this.resultsTitle) {
      this.resultsTitle.textContent = `Produkte für „${query}“`;
    }

    if (this.resultsCount) {
      this.resultsCount.hidden = true;
      this.resultsCount.textContent = "";
    }

    if (!this.liveResults) return;

    const skeleton = document.createElement("div");
    skeleton.className = "kf-search-skeleton";
    skeleton.setAttribute("aria-hidden", "true");

    for (let index = 0; index < 3; index += 1) {
      const row = document.createElement("div");
      row.className = "kf-search-skeleton__row";

      const image = document.createElement("span");
      image.className = "kf-search-skeleton__image";

      const content = document.createElement("span");
      content.className = "kf-search-skeleton__content";

      const title = document.createElement("span");
      title.className = "kf-search-skeleton__line";

      const price = document.createElement("span");
      price.className =
        "kf-search-skeleton__line kf-search-skeleton__line--short";

      content.append(title, price);
      row.append(image, content);
      skeleton.append(row);
    }

    this.liveResults.replaceChildren(skeleton);

    if (this.status) {
      this.status.textContent = `Produkte für ${query} werden geladen.`;
    }
  }

  async fetchProducts(query) {
    if (!this.liveResults || !this.input) return;

    this.abortController = new AbortController();
    const requestQuery = query.trim();

    try {
      const root = window.Shopify?.routes?.root || "/";
      const requestUrl = new URL(
        `${root}search/suggest.json`,
        window.location.origin,
      );
      requestUrl.searchParams.set("q", requestQuery);
      requestUrl.searchParams.set("resources[type]", "product");
      requestUrl.searchParams.set(
        "resources[limit]",
        String(this.resultsLimit),
      );
      requestUrl.searchParams.set("resources[limit_scope]", "each");
      requestUrl.searchParams.set(
        "resources[options][unavailable_products]",
        "last",
      );

      const response = await fetch(requestUrl.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Search request failed with ${response.status}`);
      }

      const payload = await response.json();
      const products = payload?.resources?.results?.products || [];

      if (this.input.value.trim() !== requestQuery) return;

      this.renderProducts(products, requestQuery);
    } catch (error) {
      if (error.name === "AbortError") return;
      this.renderError(requestQuery);
    }
  }

  renderProducts(products, query) {
    if (!this.liveResults) return;

    if (this.resultsTitle) {
      this.resultsTitle.textContent = `Produkte für „${query}“`;
    }

    if (this.resultsCount) {
      this.resultsCount.hidden = false;
      this.resultsCount.textContent = `${products.length} Treffer`;
    }

    if (products.length === 0) {
      const empty = document.createElement("p");
      empty.className = "kf-search-results__empty";
      empty.textContent = `Keine passenden Produkte für „${query}“ gefunden.`;
      this.liveResults.replaceChildren(empty);

      if (this.status) {
        this.status.textContent = `Keine Produkte für ${query} gefunden.`;
      }
      return;
    }

    const list = document.createElement("ul");
    list.className = "kf-search-product-list";
    list.setAttribute("role", "list");

    products.forEach((product) => {
      list.append(this.createProductItem(product));
    });

    this.liveResults.replaceChildren(list);

    if (this.status) {
      this.status.textContent = `${products.length} Produkte für ${query} gefunden.`;
    }
  }

  createProductItem(product) {
    const item = document.createElement("li");
    const link = document.createElement("a");
    link.className = "kf-search-product";
    link.href = product.url;

    const media = document.createElement("span");
    media.className = "kf-search-product__media";

    const imageUrl = product.featured_image?.url || product.image;
    if (imageUrl) {
      const image = document.createElement("img");
      image.className = "kf-search-product__image";
      image.src = imageUrl;
      image.alt = product.featured_image?.alt || product.title || "";
      image.loading = "lazy";
      image.width = 180;
      image.height = 180;
      media.append(image);
    } else {
      const fallback = document.createElement("span");
      fallback.className = "kf-search-product__fallback";
      fallback.textContent = (product.title || "P").slice(0, 1).toUpperCase();
      media.append(fallback);
    }

    const content = document.createElement("span");
    content.className = "kf-search-product__content";

    const title = document.createElement("span");
    title.className = "kf-search-product__title";
    title.textContent = product.title || "Produkt";

    const priceRow = document.createElement("span");
    priceRow.className = "kf-search-product__price-row";

    const price = document.createElement("span");
    price.className = "kf-search-product__price";

    const priceMin = this.toNumber(product.price_min ?? product.price);
    price.innerHTML =
      `<span class="kf-search-product__price-prefix">ab</span> ` +
      `<span class="kf-search-product__price-value">${this.formatPriceNumber(priceMin)}</span> ` +
      `<span class="kf-search-product__price-currency">€</span>`;
    priceRow.append(price);

    const compareAt = Math.max(
      this.toNumber(product.compare_at_price_min),
      this.toNumber(product.compare_at_price_max),
    );
    if (compareAt > priceMin) {
      const comparePrice = document.createElement("span");
      comparePrice.className = "kf-search-product__compare-price";
      comparePrice.textContent = `${this.formatPriceNumber(compareAt)} €`;
      priceRow.append(comparePrice);
    }

    if (product.available === false) {
      const availability = document.createElement("span");
      availability.className = "kf-search-product__availability";
      availability.textContent = "Ausverkauft";
      priceRow.append(availability);
    }

    content.append(title, priceRow);

    const chevron = document.createElement("span");
    chevron.className = "kf-search-product__chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" focusable="false"><path d="M7.5 4.5L13 10L7.5 15.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

    link.append(media, content, chevron);
    item.append(link);

    return item;
  }

  renderError(query) {
    if (!this.liveResults) return;

    const errorMessage = document.createElement("p");
    errorMessage.className = "kf-search-results__error";
    errorMessage.textContent =
      "Die Produktsuche konnte gerade nicht geladen werden. Drücke Enter, um die vollständige Suche zu öffnen.";
    this.liveResults.replaceChildren(errorMessage);

    if (this.resultsCount) {
      this.resultsCount.hidden = true;
    }

    if (this.status) {
      this.status.textContent = `Fehler beim Laden der Produkte für ${query}.`;
    }
  }

  toNumber(value) {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  formatMoney(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
        style: "currency",
        currency: this.currency,
      }).format(value);
    } catch (_error) {
      return `${value.toFixed(2)} ${this.currency}`;
    }
  }

  formatPriceNumber(value) {
    try {
      return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (_error) {
      return value.toFixed(2).replace(".", ",");
    }
  }
}

if (!customElements.get("search-overlay")) {
  customElements.define("search-overlay", SearchOverlay);
}
